import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BudgetsPage = () => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [categories, setCategories] = useState([]); // [['food_dining','Food & Dining'], ...]
  const [subcategories, setSubcategories] = useState({}); // { parentValue: [{value,label}] }
  const [expandedParents, setExpandedParents] = useState(new Set()); // Set of parent category values that are expanded

  // Form state
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [editId, setEditId] = useState(null);
  const [budgetName, setBudgetName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amounts, setAmounts] = useState({}); // { category_value: number_string }
  const [subAmounts, setSubAmounts] = useState({}); // { parent: { childValue: number_string } }
  const [frequencies, setFrequencies] = useState({}); // { category_value: { count: string, unit: string } }
  const [monthlyOverrides, setMonthlyOverrides] = useState({}); // { category_value: { "YYYY-MM": number_string } }
  const [useMonthlyOverrides, setUseMonthlyOverrides] = useState({}); // { category_value: boolean }
  const [splits, setSplits] = useState({}); // { key: [{ id, amount, freq: { count, unit } }] }
  const [subSplits, setSubSplits] = useState({}); // { parent: { child: [{ id, amount, freq }] } }

  // Debt Payment section state
  const [debtPaymentExpanded, setDebtPaymentExpanded] = useState(false);
  const [eligibleDebts, setEligibleDebts] = useState([]);
  const [debtAmounts, setDebtAmounts] = useState({});
  const [debtFrequencies, setDebtFrequencies] = useState({});
  const [debtPaymentDays, setDebtPaymentDays] = useState({});
  const [loadingDebts, setLoadingDebts] = useState(false);

  const nameInputRef = useRef(null);

  const loadBudgets = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get('/api/income_statement/budgets/');
      setBudgets(res.data || []);
    } catch (e) {
      setError('Failed to load budgets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBudgets(); }, []);

  // Convert an amount occurring every {count} {unit} to a monthly figure
  const toMonthly = (amount, freq) => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt)) return 0;
    const count = Math.max(1, parseInt(freq?.count, 10) || 1);
    const unit = (freq?.unit || 'months');
    // Use 4 weeks per month for budgeting to match expected monthly scaling (e.g., every 3 weeks -> * 4/3)
    const WEEKS_PER_MONTH = 4;
    // Approximate days per month as 30
    const DAYS_PER_MONTH = 30;
    switch (unit) {
      case 'days':
        return amt * (DAYS_PER_MONTH / count);
      case 'weeks':
        return amt * (WEEKS_PER_MONTH / count);
      case 'months':
        return amt * (1 / count);
      case 'years':
        return amt * (1 / (12 * count));
      default:
        // Fallback: assume monthly cadence and scale by count (same as 'months' case)
        return amt * (1 / count)
    }
  };

  // Convert a monthly amount back into the amount that occurs every {count} {unit}
  const fromMonthly = (monthlyAmount, freq) => {
    const m = parseFloat(monthlyAmount);
    if (!Number.isFinite(m)) return 0;
    const count = Math.max(1, parseInt(freq?.count, 10) || 1);
    const unit = (freq?.unit || 'months');
    const WEEKS_PER_MONTH = 4;
    const DAYS_PER_MONTH = 30;
    switch (unit) {
      case 'days':
        return m * (count / DAYS_PER_MONTH);
      case 'weeks':
        return m * (count / WEEKS_PER_MONTH);
      case 'months':
        return m * count;
      case 'years':
        return m * (12 * count);
      default:
        return m * count;
    }
  };

  // Sum all children (amount with their own frequencies) into a single monthly total for a parent
  // Accept optional snapshots to avoid stale state during batched updates
  const childrenMonthlyTotal = (parentKey, subSnap = subAmounts, freqSnap = frequencies) => {
    const kids = subcategories[parentKey] || [];
    if (!kids.length) return 0;
    let total = 0;
    kids.forEach(ch => {
      const rawAmt = subSnap[parentKey]?.[ch.value] ?? '';
      const freq = freqSnap[ch.value] || { count: '1', unit: 'months' };
      total += toMonthly(rawAmt, freq);
    });
    return total;
  };

  // Recompute parent amount from its children using current parent frequency
  // Accept optional snapshots to ensure deterministic calculations
  const recomputeParentFromChildren = (parentKey, subSnap = subAmounts, freqSnap = frequencies) => {
    const kids = subcategories[parentKey] || [];
    if (!kids.length) return; // nothing to do
    const parentFreq = freqSnap[parentKey] || { count: '1', unit: 'months' };
    const monthly = childrenMonthlyTotal(parentKey, subSnap, freqSnap);
    const parentAmount = fromMonthly(monthly, parentFreq);
    setAmounts(prev => ({ ...prev, [parentKey]: parentAmount ? String(Number(parentAmount.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  // --- Splits helpers for leaf categories (no children) ---
  const splitsMonthlyTotal = (key, listSnap = null) => {
    const list = listSnap ?? (splits[key] || []);
    if (!list.length) return 0;
    return list.reduce((sum, row) => sum + toMonthly(row.amount, row.freq || { count: '1', unit: 'months' }), 0);
  };

  const recomputeFromSplits = (key, listSnap = null, freqSnap = frequencies) => {
    const monthly = splitsMonthlyTotal(key, listSnap);
    const freq = freqSnap[key] || { count: '1', unit: 'months' };
    const amt = fromMonthly(monthly, freq);
    setAmounts(prev => ({ ...prev, [key]: amt ? String(Number(amt.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const splitCategory = (key) => {
    let nextForKey = null;
    setSplits(prev => {
      const existing = prev[key] || [];
      if (existing.length) {
        // Add a blank split row
        const newRow = { id: `${Date.now()}_${Math.random()}`, amount: '', freq: { count: '1', unit: 'months' } };
        nextForKey = [...existing, newRow];
        return { ...prev, [key]: nextForKey };
      }
      // Initialize with current base value then add a blank row
      const baseAmt = amounts[key] ?? '';
      const baseFreq = frequencies[key] || { count: '1', unit: 'months' };
      const a = { id: `${Date.now()}_${Math.random()}`, amount: baseAmt, freq: { count: String(baseFreq.count || '1'), unit: baseFreq.unit || 'months' } };
      const b = { id: `${Date.now()+1}_${Math.random()}`, amount: '', freq: { count: '1', unit: 'months' } };
      nextForKey = [a, b];
      return { ...prev, [key]: nextForKey };
    });
    Promise.resolve().then(() => recomputeFromSplits(key, nextForKey, frequencies));
  };

  const getBudgetMonths = () => {
    if (!startDate) return [];
    const start = new Date(startDate + 'T12:00:00');
    const end = endDate ? new Date(endDate + 'T12:00:00') : new Date(start);
    if (!endDate) end.setFullYear(start.getFullYear() + 1);

    const months = [];
    let curr = new Date(start.getFullYear(), start.getMonth(), 1);
    const stop = new Date(end.getFullYear(), end.getMonth(), 1);

    while (curr <= stop) {
      const y = curr.getFullYear();
      const m = String(curr.getMonth() + 1).padStart(2, '0');
      months.push(`${y}-${m}`);
      curr.setMonth(curr.getMonth() + 1);
      if (months.length > 60) break; // Safety
    }
    return months;
  };

  const updateSplit = (key, id, field, value) => {
    let nextList = null;
    setSplits(prev => {
      const list = prev[key] || [];
      nextList = list.map(row => {
        if (row.id !== id) return row;
        if (field === 'amount') return { ...row, amount: value };
        if (field === 'count') return { ...row, freq: { ...(row.freq || {}), count: value } };
        if (field === 'unit') return { ...row, freq: { ...(row.freq || {}), unit: value } };
        return row;
      });
      return { ...prev, [key]: nextList };
    });
    Promise.resolve().then(() => recomputeFromSplits(key, nextList, frequencies));
  };

  const removeSplit = (key, id) => {
    let nextMap = null;
    let nextList = null;
    setSplits(prev => {
      const list = prev[key] || [];
      nextList = list.filter(row => row.id !== id);
      nextMap = { ...prev };
      if (nextList.length) nextMap[key] = nextList; else delete nextMap[key];
      return nextMap;
    });
    Promise.resolve().then(() => recomputeFromSplits(key, nextList || [], frequencies));
  };

  // --- Splits helpers for sub-categories ---
  const childSplitsMonthlyTotal = (parent, childKey, listSnap = null) => {
    const list = listSnap ?? ((subSplits[parent]?.[childKey]) || []);
    if (!list.length) return 0;
    return list.reduce((sum, row) => sum + toMonthly(row.amount, row.freq || { count: '1', unit: 'months' }), 0);
  };

  const recomputeChildFromSplits = (parent, childKey, listSnap = null, freqSnap = frequencies) => {
    const monthly = childSplitsMonthlyTotal(parent, childKey, listSnap);
    const childFreq = freqSnap[childKey] || { count: '1', unit: 'months' };
    const amt = fromMonthly(monthly, childFreq);
    // Build a deterministic next snapshot for subAmounts including this child's new value
    const nextChildVal = amt ? String(Number(amt.toFixed(2))) : (monthly === 0 ? '' : '0');
    const nextSub = {
      ...subAmounts,
      [parent]: { ...(subAmounts[parent] || {}), [childKey]: nextChildVal }
    };
    setSubAmounts(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] || {}), [childKey]: nextChildVal }
    }));
    // Recompute parent using the freshly built snapshot and the provided freq snapshot
    Promise.resolve().then(() => recomputeParentFromChildren(parent, nextSub, freqSnap));
  };

  const splitChild = (parent, childKey) => {
    let nextList = null;
    setSubSplits(prev => {
      const parentMap = { ...(prev[parent] || {}) };
      const existing = parentMap[childKey] || [];
      if (existing.length) {
        const newRow = { id: `${Date.now()}_${Math.random()}`, amount: '', freq: { count: '1', unit: 'months' } };
        nextList = [...existing, newRow];
        parentMap[childKey] = nextList;
      } else {
        const baseAmt = (subAmounts[parent]?.[childKey]) ?? '';
        const baseFreq = frequencies[childKey] || { count: '1', unit: 'months' };
        const a = { id: `${Date.now()}_${Math.random()}`, amount: baseAmt, freq: { count: String(baseFreq.count || '1'), unit: baseFreq.unit || 'months' } };
        const b = { id: `${Date.now()+1}_${Math.random()}`, amount: '', freq: { count: '1', unit: 'months' } };
        nextList = [a, b];
        parentMap[childKey] = nextList;
      }
      return { ...prev, [parent]: parentMap };
    });
    Promise.resolve().then(() => recomputeChildFromSplits(parent, childKey, nextList, frequencies));
  };

  const updateChildSplit = (parent, childKey, id, field, value) => {
    let nextList = null;
    setSubSplits(prev => {
      const parentMap = { ...(prev[parent] || {}) };
      const list = parentMap[childKey] || [];
      nextList = list.map(row => {
        if (row.id !== id) return row;
        if (field === 'amount') return { ...row, amount: value };
        if (field === 'count') return { ...row, freq: { ...(row.freq || {}), count: value } };
        if (field === 'unit') return { ...row, freq: { ...(row.freq || {}), unit: value } };
        return row;
      });
      parentMap[childKey] = nextList;
      return { ...prev, [parent]: parentMap };
    });
    Promise.resolve().then(() => recomputeChildFromSplits(parent, childKey, nextList, frequencies));
  };

  const removeChildSplit = (parent, childKey, id) => {
    let nextParentMap = null;
    let nextList = null;
    setSubSplits(prev => {
      const parentMap = { ...(prev[parent] || {}) };
      const list = parentMap[childKey] || [];
      nextList = list.filter(row => row.id !== id);
      if (nextList.length) parentMap[childKey] = nextList; else delete parentMap[childKey];
      nextParentMap = { ...prev };
      if (Object.keys(parentMap).length) nextParentMap[parent] = parentMap; else delete nextParentMap[parent];
      return nextParentMap;
    });
    Promise.resolve().then(() => recomputeChildFromSplits(parent, childKey, nextList || [], frequencies));
  };

  const computeMonthlyTotal = () => {
    // For parents with children: the displayed parent amount already equals sum of children in state
    // Therefore we sum over top-level categories using their frequencies
    const months = getBudgetMonths();
    return (categories || []).reduce((sum, [val]) => {
      const key = String(val);
      if (useMonthlyOverrides[key] && months.length > 0) {
        let catSum = 0;
        months.forEach(m => {
          catSum += parseFloat(monthlyOverrides[key]?.[m] || 0);
        });
        return sum + (catSum / months.length);
      }
      const amt = amounts[key] ?? '';
      const freq = frequencies[key] || { count: '1', unit: 'months' };
      return sum + toMonthly(amt, freq);
    }, 0);
  };

  const openCreate = async () => {
    setFormMode('create');
    setEditId(null);
    setBudgetName('');
    setStartDate('');
    setEndDate('');
    setAmounts({});
    setSubAmounts({});
    setSplits({});
    setSubSplits({});
    setExpandedParents(new Set());
    setModalError('');
    setDebtAmounts({});
    setDebtFrequencies({});
    setDebtPaymentDays({});
    setDebtPaymentExpanded(false);
    setEligibleDebts([]);
    const ensured = await ensureCategories();
    const catList = ensured?.categories || categories;
    const subMap = ensured?.subcategories || subcategories;
    // Initialize amounts and sub-amounts with blanks for each category and its subcategories
    const init = {};
    const initSubs = {};
    const freqInit = {};
    (catList || []).forEach(([val]) => {
      const key = String(val);
      init[key] = '';
      freqInit[key] = { count: '1', unit: 'months' }; // default 1 month per category
      const children = subMap[key] || [];
      if (children.length) {
        initSubs[key] = {};
        children.forEach((child) => {
          initSubs[key][child.value] = '';
          // default child frequency
          freqInit[child.value] = { count: '1', unit: 'months' };
        });
      }
    });
    setAmounts(init);
    setSubAmounts(initSubs);
    setFrequencies(freqInit);
    setMonthlyOverrides({});
    setUseMonthlyOverrides({});
    setShowModal(true);
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 0);
  };

  const openEdit = async (budget, mode = 'edit') => {
    setFormMode(mode);
    setEditId(mode === 'copy' ? null : budget.id);
    setBudgetName(mode === 'copy' ? '' : (budget.name || ''));
    setStartDate(budget.start_date || '');
    setEndDate(budget.end_date || '');
    setModalError('');
    setSplits({});
    setSubSplits({});
    const ensured = await ensureCategories();
    const catList = ensured?.categories || categories;
    const subMap = ensured?.subcategories || subcategories;
    const init = {};
    const initSubs = {};
    const source = budget.category_amounts || {};
    const excluded = new Set(['transfer', 'rewards_cashback']);

    // Build sub-amounts per parent and compute parent sums when children exist
    (catList || []).forEach(([val]) => {
      const key = String(val);
      const children = subMap[key] || [];
      if (children.length) {
        initSubs[key] = {};
        let rawSum = 0;
        let anyChildSet = false;
        children.forEach((child) => {
          const cv = child.value;
          const raw = source.hasOwnProperty(cv) ? source[cv] : '';
          const str = raw === 0 ? '' : (raw !== undefined ? String(raw) : '');
          initSubs[key][cv] = str;
          const num = parseFloat(str);
          if (Number.isFinite(num)) { rawSum += num; anyChildSet = true; }
        });
        // If any child is set, prefer backend-normalized monthly map to initialize parent display; fallback to raw sum
        if (anyChildSet) {
          const monthlyMap = budget.monthly_category_amounts || {};
          let monthlySum = 0;
          children.forEach((child) => { const mv = parseFloat(monthlyMap[String(child.value)]); if (Number.isFinite(mv)) monthlySum += mv; });
          const base = Number.isFinite(monthlySum) && monthlySum > 0 ? monthlySum : rawSum;
          init[key] = base === 0 ? '' : String(Number(base.toFixed(2)));
        } else {
          const pv = source.hasOwnProperty(key) ? source[key] : '';
          init[key] = pv === 0 ? '' : (pv !== undefined ? String(pv) : '');
        }
      } else {
        const v = source.hasOwnProperty(key) ? source[key] : '';
        init[key] = v === 0 ? '' : (v !== undefined ? String(v) : '');
      }
    });

    // Also include any extra keys present in budget.category_amounts that are not part of known parents/children, except excluded
    Object.keys(source).forEach(k => {
      if (excluded.has(String(k))) return;
      const isKnownParent = (categories || []).some(([v]) => String(v) === String(k));
      const isKnownChild = Object.values(subcategories || {}).some(arr => (arr || []).some(ch => String(ch.value) === String(k)));
      if (!isKnownParent && !isKnownChild && !(k in init)) {
        init[k] = String(source[k]);
      }
    });

    setAmounts(init);
    setSubAmounts(initSubs);
    setMonthlyOverrides(budget.category_monthly_overrides || {});
    const overridesMeta = {};
    if (budget.category_monthly_overrides) {
        Object.keys(budget.category_monthly_overrides).forEach(k => {
            if (Object.keys(budget.category_monthly_overrides[k]).length > 0) {
                overridesMeta[k] = true;
            }
        });
    }
    setUseMonthlyOverrides(overridesMeta);

    // Initialize frequencies using budget data, defaulting to 1 month
    const fInit = {};
    const srcFreq = budget.category_frequencies || {};
    const allowed = new Set(['days','weeks','months','years']);
    // Ensure all categories get a frequency entry (parents)
    (catList || []).forEach(([val]) => {
      const key = String(val);
      const raw = srcFreq[key] || {};
      let count = parseInt(raw.count, 10);
      if (!Number.isFinite(count) || count <= 0) count = 1;
      let unit = raw.unit || 'months';
      unit = allowed.has(unit) ? unit : 'months';
      fInit[key] = { count: String(count), unit };
      // children frequencies
      const children = (subMap[key] || []);
      children.forEach(ch => {
        const craw = srcFreq[ch.value] || {};
        let ccount = parseInt(craw.count, 10);
        if (!Number.isFinite(ccount) || ccount <= 0) ccount = 1;
        let cunit = craw.unit || 'months';
        cunit = allowed.has(cunit) ? cunit : 'months';
        fInit[ch.value] = { count: String(ccount), unit: cunit };
      });
    });
    // Include any extra keys present in budget.category_frequencies, except excluded ones
    Object.keys(srcFreq).forEach((k) => {
      if (!(k in fInit) && !excluded.has(String(k))) {
        const raw = srcFreq[k] || {};
        let count = parseInt(raw.count, 10);
        if (!Number.isFinite(count) || count <= 0) count = 1;
        let unit = raw.unit || 'months';
        unit = allowed.has(unit) ? unit : 'months';
        fInit[k] = { count: String(count), unit };
      }
    });
    setFrequencies(fInit);

    // Load debt payment schedule from budget
    const schedule = budget.debt_payment_schedule || {};
    const dAmounts = {};
    const dFreqs = {};
    const dDays = {};
    Object.entries(schedule).forEach(([slugKey, meta]) => {
      const rawAmt = (budget.category_amounts || {})[slugKey];
      dAmounts[slugKey] = rawAmt !== undefined && rawAmt !== 0 ? String(rawAmt) : '';
      const rawFreq = (budget.category_frequencies || {})[slugKey] || {};
      dFreqs[slugKey] = { count: String(rawFreq.count || '1'), unit: rawFreq.unit || 'months' };
      dDays[slugKey] = meta.payment_day !== undefined && meta.payment_day !== null ? String(meta.payment_day) : '';
    });
    setDebtAmounts(dAmounts);
    setDebtFrequencies(dFreqs);
    setDebtPaymentDays(dDays);
    if (Object.keys(schedule).length > 0) {
      setDebtPaymentExpanded(true);
      axios.get('/api/income_statement/budgets/eligible-debts/')
        .then(res => setEligibleDebts(res.data || []))
        .catch(() => {});
    } else {
      setDebtPaymentExpanded(false);
      setEligibleDebts([]);
    }

    // After initializing frequencies, recalc parent amounts from children using fresh snapshots to respect frequency conversions
    setTimeout(() => {
      (Object.entries(subMap) || []).forEach(([p, kids]) => {
        if ((kids || []).length) {
          recomputeParentFromChildren(String(p), initSubs, fInit);
        }
      });
      if ((mode === 'copy' || mode === 'create') && nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 0);
    setShowModal(true);
  };

  const openCopy = (budget) => {
    openEdit(budget, 'copy');
  };

  // Fetch categories on-demand (when opening modal)
  const ensureCategories = async () => {
    if (categories && categories.length > 0) return { categories, subcategories };
    try {
      const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
      const raw = res.data || [];
      // Build a map of parent -> children from API metadata, while keeping a flat list for parents
      const excluded = new Set(['transfer', 'rewards_cashback', 'tv_cellphones']);
      const parentToChildren = {};
      const childValues = new Set();

      (Array.isArray(raw) ? raw : []).forEach((entry) => {
        if (entry && typeof entry === 'object' && Array.isArray(entry.children) && entry.children.length) {
          const parentVal = String(entry.value ?? '');
          parentToChildren[parentVal] = entry.children.map(ch => ({ value: String(ch.value), label: String(ch.label ?? ch.value) }));
          entry.children.forEach(ch => childValues.add(String(ch.value)));
        }
      });

      // Build flat list of top-level categories by excluding known child values and excluded set
      const normalized = (Array.isArray(raw) ? raw : []).map((entry) => {
        if (Array.isArray(entry)) {
          const [v, l] = entry; return [String(v), String(l ?? v ?? '')];
        }
        if (entry && typeof entry === 'object') {
          const v = entry.value ?? Object.keys(entry)[0];
          const l = entry.label ?? (v ?? '');
          return [String(v), String(l)];
        }
        const s = String(entry);
        return [s, s];
      });

      let filtered = (normalized || []).filter(([v]) => !excluded.has(String(v)) && !childValues.has(String(v)));
      // Sort A-Z by label
      filtered = filtered.sort((a, b) => (String(a[1] || '').localeCompare(String(b[1] || ''))));
      setCategories(filtered);
      setSubcategories(parentToChildren);
      return { categories: filtered, subcategories: parentToChildren };
    } catch (e) {
      // Fallback: minimal set
      const fallback = [["none","Uncategorized"]];
      setCategories(fallback);
      setSubcategories({});
      return { categories: fallback, subcategories: {} };
    }
  };

  const onChangeAmount = (key, value) => {
    // If this parent has subcategories, setting a parent directly should:
    // - Reset all subcats to 0
    // - Set the parent's 'Other' subcat to the entered amount
    // - Align the 'Other' child's frequency with the parent's frequency
    const hasChildren = Array.isArray(subcategories[key]) && subcategories[key].length > 0;
    if (hasChildren) {
      const children = subcategories[key] || [];
      const other = children.find(ch => ch.value === `${key}__other`) || null;
      const valStr = value;
      const num = parseFloat(valStr);
      // Reset child amounts and set Other
      setSubAmounts(prev => {
        const next = { ...prev };
        next[key] = next[key] ? { ...next[key] } : {};
        children.forEach(ch => { next[key][ch.value] = '0'; });
        if (other) {
          next[key][other.value] = Number.isFinite(num) ? String(num) : '';
        }
        return next;
      });
      // Copy parent frequency to Other child for consistency
      if (other) {
        setFrequencies(prev => {
          const pf = prev[key] || { count: '1', unit: 'months' };
          return { ...prev, [other.value]: { count: String(pf.count ?? '1'), unit: pf.unit || 'months' } };
        });
      }
      // Also set the parent amount itself
      setAmounts(prev => ({ ...prev, [key]: value }));
      return;
    }
    setAmounts(prev => ({ ...prev, [key]: value }));
  };

  const onChangeChildAmount = (parent, childValue, value) => {
    // Update a child amount and recalc parent using frequency-normalized sum with fresh snapshot
    let nextSub = null;
    setSubAmounts(prev => {
      nextSub = { ...prev };
      nextSub[parent] = nextSub[parent] ? { ...nextSub[parent] } : {};
      nextSub[parent][childValue] = value;
      return nextSub;
    });
    // Deterministically recompute using the freshly built snapshot
    // Use a microtask to allow React state to apply without relying on stale closures
    Promise.resolve().then(() => recomputeParentFromChildren(parent, nextSub, frequencies));
  };

  const toggleDebtPaymentExpand = async () => {
    if (!debtPaymentExpanded && eligibleDebts.length === 0) {
      setLoadingDebts(true);
      try {
        const res = await axios.get('/api/income_statement/budgets/eligible-debts/');
        const debts = res.data || [];
        setEligibleDebts(debts);
        setDebtAmounts(prev => {
          const next = { ...prev };
          debts.forEach(d => { const k = `payments__debt_${d.id}`; if (!(k in next)) next[k] = ''; });
          return next;
        });
        setDebtFrequencies(prev => {
          const next = { ...prev };
          debts.forEach(d => { const k = `payments__debt_${d.id}`; if (!(k in next)) next[k] = { count: '1', unit: 'months' }; });
          return next;
        });
        setDebtPaymentDays(prev => {
          const next = { ...prev };
          debts.forEach(d => { const k = `payments__debt_${d.id}`; if (!(k in next)) next[k] = ''; });
          return next;
        });
      } catch (e) {
        console.error('Failed to load eligible debts', e);
      } finally {
        setLoadingDebts(false);
      }
    }
    setDebtPaymentExpanded(prev => !prev);
  };

  const debtPaymentMonthlyTotal = () => {
    return Object.entries(debtAmounts).reduce((sum, [key, val]) => {
      const freq = debtFrequencies[key] || { count: '1', unit: 'months' };
      return sum + toMonthly(val, freq);
    }, 0);
  };

  const onChangeDebtAmount = (key, value) => {
    const nextDebtAmounts = { ...debtAmounts, [key]: value };
    setDebtAmounts(nextDebtAmounts);
    const monthly = Object.entries(nextDebtAmounts).reduce((sum, [k, v]) => {
      const freq = debtFrequencies[k] || { count: '1', unit: 'months' };
      return sum + toMonthly(v, freq);
    }, 0);
    const parentFreq = frequencies['payments'] || { count: '1', unit: 'months' };
    const parentAmt = fromMonthly(monthly, parentFreq);
    setAmounts(prev => ({ ...prev, payments: parentAmt ? String(Number(parentAmt.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const onChangeDebtFrequency = (key, field, value) => {
    const nextFreqs = { ...debtFrequencies, [key]: { ...(debtFrequencies[key] || {}), [field]: value } };
    setDebtFrequencies(nextFreqs);
    const monthly = Object.entries(debtAmounts).reduce((sum, [k, v]) => {
      const freq = nextFreqs[k] || { count: '1', unit: 'months' };
      return sum + toMonthly(v, freq);
    }, 0);
    const parentFreq = frequencies['payments'] || { count: '1', unit: 'months' };
    const parentAmt = fromMonthly(monthly, parentFreq);
    setAmounts(prev => ({ ...prev, payments: parentAmt ? String(Number(parentAmt.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const toggleExpand = (parent) => {
    setExpandedParents(prev => {
      const next = new Set(Array.from(prev));
      if (next.has(parent)) next.delete(parent); else next.add(parent);
      return next;
    });
  };

  const submit = async () => {
    setSaving(true); setModalError('');

    // Name uniqueness validation for Copy and Create modes
    if (formMode === 'create' || formMode === 'copy' || formMode === 'edit') {
      const trimmedName = (budgetName || '').trim();
      if (trimmedName) {
        const existing = budgets.find(b => 
          (b.name || '').trim().toLowerCase() === trimmedName.toLowerCase() &&
          (formMode === 'edit' ? b.id !== editId : true)
        );
        if (existing) {
          setModalError(`A budget with the name "${trimmedName}" already exists. Please choose a different name.`);
          setSaving(false);
          return;
        }
      }
    }

    try {
      if (!startDate) {
        setModalError('Start date is required.'); setSaving(false); return;
      }
      // Build category_amounts map (numbers, default 0 for blanks)
      const payloadMap = {};
      // First include parent values
      Object.entries(amounts || {}).forEach(([k, v]) => {
        const num = parseFloat(v);
        payloadMap[k] = Number.isFinite(num) ? num : 0;
      });
      // Include children values per parent; ensure parent equals sum of its children (converted to parent's frequency)
      Object.entries(subAmounts || {}).forEach(([parent, childMap]) => {
        const children = subcategories[parent] || [];
        if (!children.length) return;
        let monthlySum = 0;
        children.forEach((ch) => {
          const raw = (childMap || {})[ch.value];
          const num = parseFloat(raw);
          const val = Number.isFinite(num) ? num : 0;
          payloadMap[ch.value] = val;
          // Convert each child to monthly using its own frequency map
          const cf = frequencies[ch.value] || { count: '1', unit: 'months' };
          monthlySum += toMonthly(val, cf);
        });
        // Convert monthly back into parent's frequency for stored parent value
        const pf = frequencies[parent] || { count: '1', unit: 'months' };
        const parentVal = fromMonthly(monthlySum, pf);
        payloadMap[parent] = Number.isFinite(parentVal) ? Number(parentVal.toFixed(2)) : 0;
      });
      // Build category_frequencies map with sane defaults and validation
      const freqMap = {};
      const allowed = new Set(['days','weeks','months','years']);
      // Ensure we cover all known keys from amounts and frequencies
      const keys = new Set([...Object.keys(amounts || {}), ...Object.keys(frequencies || {}), ...Object.keys(monthlyOverrides || {})]);
      keys.forEach((k) => {
        const raw = (frequencies || {})[k] || {};
        let count = parseInt(raw.count, 10);
        if (!Number.isFinite(count) || count <= 0) count = 1;
        let unit = (raw.unit || 'months');
        unit = allowed.has(unit) ? unit : 'months';
        freqMap[k] = { count, unit };
      });

      // Build category_monthly_overrides map
      const overridesMap = {};
      Object.entries(useMonthlyOverrides || {}).forEach(([k, use]) => {
        if (use && monthlyOverrides[k]) {
          const catOverrides = {};
          Object.entries(monthlyOverrides[k]).forEach(([month, val]) => {
            const num = parseFloat(val);
            if (Number.isFinite(num)) {
              catOverrides[month] = num;
            }
          });
          if (Object.keys(catOverrides).length > 0) {
            overridesMap[k] = catOverrides;
          }
        }
      });

      // Merge debt child amounts and frequencies into payloadMap / freqMap
      Object.entries(debtAmounts).forEach(([k, v]) => {
        const num = parseFloat(v);
        payloadMap[k] = Number.isFinite(num) ? num : 0;
      });
      Object.entries(debtFrequencies).forEach(([k, freq]) => {
        let count = parseInt(freq.count, 10);
        if (!Number.isFinite(count) || count <= 0) count = 1;
        let unit = freq.unit || 'months';
        if (!allowed.has(unit)) unit = 'months';
        freqMap[k] = { count, unit };
      });
      // Build debt_payment_schedule
      const debtSchedule = {};
      eligibleDebts.forEach(d => {
        const k = `payments__debt_${d.id}`;
        const amt = parseFloat(debtAmounts[k]);
        if (Number.isFinite(amt) && amt > 0) {
          debtSchedule[k] = {
            debt_id: d.id,
            debt_type: d.debt_type,
            debt_name: d.name,
            payment_day: debtPaymentDays[k] ? parseInt(debtPaymentDays[k], 10) : null,
          };
        }
      });

      const monthly_total = computeMonthlyTotal();
      const payload = {
        name: budgetName || null,
        start_date: startDate,
        category_amounts: payloadMap,
        category_frequencies: freqMap,
        category_monthly_overrides: overridesMap,
        monthly_total: Number.isFinite(monthly_total) ? Number(monthly_total.toFixed(2)) : 0,
        debt_payment_schedule: debtSchedule,
      };
      if (endDate) payload.end_date = endDate;

      if (formMode === 'create' || formMode === 'copy') {
        await axios.post('/api/income_statement/budgets/', payload);
      } else if (formMode === 'edit' && editId != null) {
        await axios.patch(`/api/income_statement/budgets/${editId}/`, payload);
      }
      setShowModal(false);
      await loadBudgets();
    } catch (e) {
      const msg = e.response?.data || e.message;
      setModalError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content-container">
      <h1>Budgets</h1>

      <div className="table-actions" style={{ justifyContent: 'flex-start' }}>
        <button className="btn-primary" onClick={openCreate}>Create New Budget</button>
      </div>

      {loading && <div>Loading budgets…</div>}
      {error && <div style={{ color: '#fca5a5' }}>{error}</div>}

      {!loading && !error && (
        <div className="data-table-container">
          <table style={{ width:'100%', borderCollapse:'collapse', color:'#e5e7eb' }}>
            <thead style={{ background:'#1f2937' }}>
              <tr>
                <th style={{ textAlign:'left', padding:'8px' }}>Budget Name</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Start Date</th>
                <th style={{ textAlign:'left', padding:'8px' }}>End Date</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Categories Set</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Total (Monthly)</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Created At</th>
                <th style={{ textAlign:'center', padding:'8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {budgets.length === 0 && (
                <tr><td colSpan={7} style={{ padding:'10px', color:'#9ca3af' }}>No budgets yet.</td></tr>
              )}
              {budgets.map((b, idx) => (
                <tr key={b.id || idx} style={{ backgroundColor: idx % 2 ? '#0f172a' : '#111827' }}>
                  <td style={{ padding:'8px', cursor:'pointer', color:'#60a5fa' }} onClick={() => navigate(`/income_statement/budgets/${b.id}`)}>{(b.name && String(b.name).trim().length) ? b.name : '(unnamed)'}</td>
                  <td style={{ padding:'8px' }}>{b.start_date}</td>
                  <td style={{ padding:'8px' }}>{b.end_date || '(open)'}</td>
                  <td style={{ padding:'8px' }}>{b.category_amounts ? Object.keys(b.category_amounts).length : 0}</td>
                  <td style={{ padding:'8px' }}>{(() => {
                    const mt = b.monthly_total;
                    if (mt === null || mt === undefined) return '—';
                    const num = Number(mt);
                    if (!Number.isFinite(num)) return '—';
                    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  })()}</td>
                  <td style={{ padding:'8px' }}>{(() => { try { const d = new Date(b.created_at); return isNaN(d.getTime()) ? '' : d.toLocaleString(); } catch(e) { return b.created_at || ''; } })()}</td>
                  <td style={{ padding:'8px', textAlign:'center' }}>
                    <button 
                      className="btn-secondary" 
                      style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                      onClick={(e) => { e.stopPropagation(); openCopy(b); }}
                    >
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(900px, 95vw)', maxHeight:'85vh', display:'flex', flexDirection:'column', boxShadow:'0 10px 30px rgba(0,0,0,0.5)' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <h3 style={{ margin:0 }}>{formMode === 'create' ? 'Create New Budget' : (formMode === 'copy' ? 'Copy Budget' : 'Edit Budget')}</h3>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>✕</button>
            </div>

            <div style={{ padding:16, display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap', justifyContent:'space-between' }}>
              <div style={{ minWidth: 280, flex: '1 1 280px' }}>
                <label className="control-label">Budget Name</label><br/>
                <input 
                  type="text" 
                  className="control-select" 
                  value={budgetName} 
                  onChange={e => setBudgetName(e.target.value)} 
                  placeholder="e.g., 2025 Family Budget" 
                  ref={nameInputRef}
                />
              </div>
              <div style={{ minWidth: 280, textAlign:'right', flex: '1 1 280px' }}>
                <div style={{ fontWeight:800, fontSize: '1.1rem' }}>
                  {(() => {
                    const monthly = computeMonthlyTotal();
                    return <span>Total Budget (per month): ${monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
                  })()}
                </div>
              </div>
            </div>

            <div style={{ padding:16, display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div style={{ minWidth: 220 }}>
                <label className="control-label">Start Date</label><br/>
                <input type="date" className="control-select" value={startDate} onChange={e => setStartDate(e.target.value)} required />
              </div>
              <div style={{ minWidth: 220 }}>
                <label className="control-label">End Date (optional)</label><br/>
                <input type="date" className="control-select" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>

            <div style={{ padding:'0 16px 16px 16px' }}>

              <div style={{ fontWeight:600, marginBottom:8 }}>Expense Categories</div>
              <div style={{ border:'1px solid #374151', borderRadius:6, maxHeight:'45vh', overflowY:'auto' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1.2fr 0.8fr 0.6fr 0.8fr 90px 80px', gap:8, padding:'8px 12px', position:'sticky', top:0, background:'#111827', borderBottom:'1px solid #374151' }}>
                  <div>Category</div>
                  <div>Amount ($)</div>
                  <div>Every</div>
                  <div>Frequency</div>
                  <div>Mode</div>
                  <div></div>
                </div>
                <div>
                  {(categories || []).map(([val, label]) => {
                    const key = String(val);
                    const isDebtPayment = key === 'payments';
                    const children = subcategories[key] || [];
                    const hasChildren = children.length > 0;
                    const expanded = expandedParents.has(key);
                    return (
                      <React.Fragment key={key}>
                        <div style={{ display:'grid', gridTemplateColumns:'1.2fr 0.8fr 0.6fr 0.8fr 90px 80px', gap:8, alignItems:'center', padding:'8px 12px', borderBottom:'1px solid #111827' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {isDebtPayment ? (
                              <button
                                onClick={toggleDebtPaymentExpand}
                                className="btn btn-secondary"
                                style={{ width:24, height:24, borderRadius:9999, background:'#111827', border:'1px solid #374151', color:'#93c5fd', display:'inline-flex', alignItems:'center', justifyContent:'center', boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                                onMouseEnter={(e)=> e.currentTarget.style.backgroundColor = '#1f2937'}
                                onMouseLeave={(e)=> e.currentTarget.style.backgroundColor = '#111827'}
                                title={debtPaymentExpanded ? 'Collapse debt obligations' : 'Expand debt obligations'}
                              >
                                {debtPaymentExpanded ? '−' : '+'}
                              </button>
                            ) : hasChildren ? (
                              <button
                                onClick={() => toggleExpand(key)}
                                className="btn btn-secondary"
                                style={{ width:24, height:24, borderRadius:9999, background:'#111827', border:'1px solid #374151', color:'#93c5fd', display:'inline-flex', alignItems:'center', justifyContent:'center', boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                                onMouseEnter={(e)=> e.currentTarget.style.backgroundColor = '#1f2937'}
                                onMouseLeave={(e)=> e.currentTarget.style.backgroundColor = '#111827'}
                                title={expanded ? 'Collapse' : 'Expand'}
                              >
                                {expanded ? '−' : '+'}
                              </button>
                            ) : (
                              <span aria-hidden="true" style={{ width:24, height:24, display:'inline-block' }}></span>
                            )}
                            <span>{label}</span>
                          </div>
                          <div>
                            {!useMonthlyOverrides[key] ? (
                              <input
                                type="number"
                                step="0.01"
                                value={amounts[key] ?? ''}
                                onChange={e => onChangeAmount(key, e.target.value)}
                                className="control-select"
                                style={{ width:'100%' }}
                                placeholder="0.00"
                                disabled={(isDebtPayment && debtPaymentExpanded && eligibleDebts.length > 0) || (!hasChildren && Boolean((splits[key] || []).length))}
                                title={isDebtPayment && debtPaymentExpanded ? 'Auto-calculated from debt obligations below' : undefined}
                              />
                            ) : (
                              <div style={{ fontSize:11, color:'#9ca3af' }}>Monthly values below</div>
                            )}
                          </div>
                          <div>
                            {!useMonthlyOverrides[key] ? (
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={(frequencies[key]?.count) ?? '1'}
                                onChange={e => {
                                  const newVal = e.target.value;
                                  let nextFreq = null;
                                  setFrequencies(prev => {
                                    nextFreq = { ...prev, [key]: { ...(prev[key] || {}), count: newVal } };
                                    return nextFreq;
                                  });
                                  // Recompute aggregate if needed using fresh snapshot
                                  Promise.resolve().then(() => {
                                    if ((subcategories[key] || []).length) {
                                      recomputeParentFromChildren(key, subAmounts, nextFreq);
                                    } else if ((splits[key] || []).length) {
                                      recomputeFromSplits(key, splits[key] || [], nextFreq);
                                    }
                                  });
                                }}
                                className="control-select"
                                style={{ width:'100%' }}
                                placeholder="1"
                              />
                            ) : (
                              <div style={{ textAlign:'center' }}>—</div>
                            )}
                          </div>
                          <div>
                            {!useMonthlyOverrides[key] ? (
                              <select
                                className="control-select"
                                value={(frequencies[key]?.unit) ?? 'months'}
                                onChange={e => {
                                  const newUnit = e.target.value;
                                  let nextFreq = null;
                                  setFrequencies(prev => {
                                    nextFreq = { ...prev, [key]: { ...(prev[key] || {}), unit: newUnit } };
                                    return nextFreq;
                                  });
                                  Promise.resolve().then(() => {
                                    if ((subcategories[key] || []).length) {
                                      recomputeParentFromChildren(key, subAmounts, nextFreq);
                                    } else if ((splits[key] || []).length) {
                                      recomputeFromSplits(key, splits[key] || [], nextFreq);
                                    }
                                  });
                                }}
                                style={{ width:'100%' }}
                              >
                                <option value="days">days</option>
                                <option value="weeks">weeks</option>
                                <option value="months">months</option>
                                <option value="years">years</option>
                              </select>
                            ) : (
                              <div style={{ textAlign:'center' }}>—</div>
                            )}
                          </div>
                          <div>
                            {!hasChildren && (
                              <select
                                className="control-select"
                                style={{ width:'100%', fontSize:12 }}
                                value={useMonthlyOverrides[key] ? 'monthly' : 'fixed'}
                                onChange={e => {
                                  const val = e.target.value;
                                  setUseMonthlyOverrides(prev => ({ ...prev, [key]: val === 'monthly' }));
                                }}
                              >
                                <option value="fixed">Fixed</option>
                                <option value="monthly">Monthly</option>
                              </select>
                            )}
                          </div>
                          <div>
                            {!hasChildren && !useMonthlyOverrides[key] && (
                              <button
                                onClick={() => splitCategory(key)}
                                className="btn btn-secondary"
                                style={{ padding:'2px 10px', height:24, borderRadius:9999, background:'#0f172a', border:'1px solid #374151', color:'#e5e7eb', fontSize:12, letterSpacing:0.2, boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                                onMouseEnter={(e)=> e.currentTarget.style.backgroundColor = '#1f2937'}
                                onMouseLeave={(e)=> e.currentTarget.style.backgroundColor = '#0f172a'}
                                title="Split"
                              >split</button>
                            )}
                          </div>
                        </div>
                        {useMonthlyOverrides[key] && (
                          <div style={{ padding:'8px 12px 12px 40px', background:'#0b1220', borderBottom:'1px solid #111827' }}>
                            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:8 }}>Monthly Overrides</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:12 }}>
                              {getBudgetMonths().map(month => (
                                <div key={month}>
                                  <label style={{ fontSize:10, display:'block', marginBottom:2, color:'#6b7280' }}>{month}</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="control-select"
                                    style={{ width:'100%', height:28, fontSize:12 }}
                                    value={monthlyOverrides[key]?.[month] ?? ''}
                                    onChange={e => {
                                      const val = e.target.value;
                                      setMonthlyOverrides(prev => ({
                                        ...prev,
                                        [key]: { ...(prev[key] || {}), [month]: val }
                                      }));
                                    }}
                                    placeholder="0.00"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {!hasChildren && (splits[key] || []).length > 0 && (
                          <div style={{ padding:'0 12px 8px 40px', background:'#0b1220', borderBottom:'1px solid #111827' }}>
                            {(splits[key] || []).map(row => (
                              <div key={row.id} style={{ display:'grid', gridTemplateColumns:'1fr 0.5fr 0.7fr auto', gap:8, alignItems:'center', padding:'6px 0' }}>
                                <div style={{ color:'#9ca3af' }}>• Split</div>
                                <div>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={row.amount ?? ''}
                                    onChange={e => updateSplit(key, row.id, 'amount', e.target.value)}
                                    className="control-select"
                                    style={{ width:'100%' }}
                                    placeholder="0.00"
                                  />
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'0.6fr 1fr', gap:8 }}>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={(row.freq?.count) ?? '1'}
                                    onChange={e => updateSplit(key, row.id, 'count', e.target.value)}
                                    className="control-select"
                                    placeholder="1"
                                  />
                                  <select
                                    className="control-select"
                                    value={(row.freq?.unit) ?? 'months'}
                                    onChange={e => updateSplit(key, row.id, 'unit', e.target.value)}
                                  >
                                    <option value="days">days</option>
                                    <option value="weeks">weeks</option>
                                    <option value="months">months</option>
                                    <option value="years">years</option>
                                  </select>
                                </div>
                                <div>
                                  <button className="btn btn-secondary" onClick={() => removeSplit(key, row.id)} title="Remove">×</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {isDebtPayment && debtPaymentExpanded && (
                          <div style={{ padding:'0 12px 8px 12px', background:'#0b1220', borderBottom:'1px solid #111827' }}>
                            {loadingDebts && <div style={{ color:'#9ca3af', padding:'8px 0', fontSize:13 }}>Loading debt obligations…</div>}
                            {!loadingDebts && eligibleDebts.length === 0 && (
                              <div style={{ color:'#9ca3af', padding:'8px 0', fontSize:13 }}>No eligible debts found (mortgages, auto loans, HELOCs).</div>
                            )}
                            {!loadingDebts && eligibleDebts.length > 0 && (
                              <>
                                <div style={{ display:'grid', gridTemplateColumns:'1.4fr 0.8fr 0.5fr 0.7fr 80px', gap:8, padding:'6px 0 4px 0', fontSize:11, color:'#6b7280', borderBottom:'1px solid #1f2937' }}>
                                  <div>Debt Obligation</div>
                                  <div>Payment Amount ($)</div>
                                  <div>Every</div>
                                  <div>Frequency</div>
                                  <div>Pay Day</div>
                                </div>
                                {eligibleDebts.map(debt => {
                                  const dk = `payments__debt_${debt.id}`;
                                  return (
                                    <div key={dk} style={{ display:'grid', gridTemplateColumns:'1.4fr 0.8fr 0.5fr 0.7fr 80px', gap:8, alignItems:'center', padding:'6px 0', borderBottom:'1px solid #111827' }}>
                                      <div style={{ color:'#d1d5db', fontSize:13 }}>
                                        ↳ <span style={{ fontWeight:500 }}>{debt.name}</span>
                                        <span style={{ marginLeft:6, fontSize:11, color:'#6b7280', textTransform:'capitalize' }}>({debt.debt_type.replace('_', ' ')})</span>
                                      </div>
                                      <div>
                                        <input
                                          type="number" step="0.01"
                                          value={debtAmounts[dk] ?? ''}
                                          onChange={e => onChangeDebtAmount(dk, e.target.value)}
                                          className="control-select" style={{ width:'100%' }}
                                          placeholder="0.00"
                                        />
                                      </div>
                                      <div>
                                        <input
                                          type="number" min="1" step="1"
                                          value={debtFrequencies[dk]?.count ?? '1'}
                                          onChange={e => onChangeDebtFrequency(dk, 'count', e.target.value)}
                                          className="control-select" style={{ width:'100%' }}
                                          placeholder="1"
                                        />
                                      </div>
                                      <div>
                                        <select
                                          className="control-select" style={{ width:'100%' }}
                                          value={debtFrequencies[dk]?.unit ?? 'months'}
                                          onChange={e => onChangeDebtFrequency(dk, 'unit', e.target.value)}
                                        >
                                          <option value="days">days</option>
                                          <option value="weeks">weeks</option>
                                          <option value="months">months</option>
                                          <option value="years">years</option>
                                        </select>
                                      </div>
                                      <div>
                                        <input
                                          type="number" min="1" max="31" step="1"
                                          value={debtPaymentDays[dk] ?? ''}
                                          onChange={e => setDebtPaymentDays(prev => ({ ...prev, [dk]: e.target.value }))}
                                          className="control-select" style={{ width:'100%' }}
                                          placeholder="Day #"
                                          title="Day of month payment is expected (e.g. 4 = 4th of each month)"
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                                <div style={{ fontSize:11, color:'#6b7280', marginTop:6 }}>
                                  Total Debt Payment (monthly): <strong style={{ color:'#e5e7eb' }}>${debtPaymentMonthlyTotal().toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 })}</strong>
                                  &nbsp;·&nbsp; "Pay Day" = day of month payment is expected (e.g. 4 = 4th)
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {!isDebtPayment && hasChildren && expanded && (
                          <div style={{ padding:'0 12px 8px 12px', background:'#0b1220', borderBottom:'1px solid #111827' }}>
                            {children.map(ch => (
                              <React.Fragment key={ch.value}>
                                <div style={{ display:'grid', gridTemplateColumns:'1.2fr 0.8fr 0.6fr 0.8fr 90px', gap:8, alignItems:'center', padding:'6px 0' }}>
                                  <div style={{ color:'#9ca3af', display:'flex', alignItems:'center', gap:6 }}>
                                    ↳ {ch.label}
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={subAmounts[key]?.[ch.value] ?? ''}
                                      onChange={e => onChangeChildAmount(key, ch.value, e.target.value)}
                                      className="control-select"
                                      style={{ width:'100%' }}
                                      placeholder="0.00"
                                      disabled={Boolean(((subSplits[key] || {})[ch.value] || []).length)}
                                    />
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={(frequencies[ch.value]?.count) ?? '1'}
                                      onChange={e => {
                                        const newVal = e.target.value;
                                        let nextFreq = null;
                                        setFrequencies(prev => {
                                          nextFreq = { ...prev, [ch.value]: { ...(prev[ch.value] || {}), count: newVal } };
                                          return nextFreq;
                                        });
                                        Promise.resolve().then(() => {
                                          const hasSplits = (((subSplits[key] || {})[ch.value] || []).length) > 0;
                                          if (hasSplits) {
                                            recomputeChildFromSplits(key, ch.value, (subSplits[key] || {})[ch.value] || [], nextFreq);
                                          }
                                          recomputeParentFromChildren(key, subAmounts, nextFreq);
                                        });
                                      }}
                                      className="control-select"
                                      style={{ width:'100%' }}
                                      placeholder="1"
                                    />
                                  </div>
                                  <div>
                                    <select
                                      className="control-select"
                                      value={(frequencies[ch.value]?.unit) ?? 'months'}
                                      onChange={e => {
                                        const newUnit = e.target.value;
                                        let nextFreq = null;
                                        setFrequencies(prev => {
                                          nextFreq = { ...prev, [ch.value]: { ...(prev[ch.value] || {}), unit: newUnit } };
                                          return nextFreq;
                                        });
                                        Promise.resolve().then(() => {
                                          const hasSplits = (((subSplits[key] || {})[ch.value] || []).length) > 0;
                                          if (hasSplits) {
                                            recomputeChildFromSplits(key, ch.value, (subSplits[key] || {})[ch.value] || [], nextFreq);
                                          }
                                          recomputeParentFromChildren(key, subAmounts, nextFreq);
                                        });
                                      }}
                                      style={{ width:'100%' }}
                                    >
                                      <option value="days">days</option>
                                      <option value="weeks">weeks</option>
                                      <option value="months">months</option>
                                      <option value="years">years</option>
                                    </select>
                                  </div>
                                  <div>
                                    <button
                                      onClick={() => splitChild(key, ch.value)}
                                      className="btn btn-secondary"
                                      style={{ padding:'2px 10px', height:22, borderRadius:9999, background:'#0f172a', border:'1px solid #374151', color:'#e5e7eb', fontSize:12, letterSpacing:0.2, boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                                      onMouseEnter={(e)=> e.currentTarget.style.backgroundColor = '#1f2937'}
                                      onMouseLeave={(e)=> e.currentTarget.style.backgroundColor = '#0f172a'}
                                      title="Split"
                                    >split</button>
                                  </div>
                                </div>
                                {Boolean(((subSplits[key] || {})[ch.value] || []).length) && (
                                  <div style={{ padding:'0 0 8px 24px' }}>
                                    {(((subSplits[key] || {})[ch.value]) || []).map(row => (
                                      <div key={row.id} style={{ display:'grid', gridTemplateColumns:'1fr 0.5fr 0.7fr auto', gap:8, alignItems:'center', padding:'4px 0' }}>
                                        <div style={{ color:'#9ca3af' }}>• Split</div>
                                        <div>
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={row.amount ?? ''}
                                            onChange={e => updateChildSplit(key, ch.value, row.id, 'amount', e.target.value)}
                                            className="control-select"
                                            style={{ width:'100%' }}
                                            placeholder="0.00"
                                          />
                                        </div>
                                        <div style={{ display:'grid', gridTemplateColumns:'0.6fr 1fr', gap:8 }}>
                                          <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={(row.freq?.count) ?? '1'}
                                            onChange={e => updateChildSplit(key, ch.value, row.id, 'count', e.target.value)}
                                            className="control-select"
                                            placeholder="1"
                                          />
                                          <select
                                            className="control-select"
                                            value={(row.freq?.unit) ?? 'months'}
                                            onChange={e => updateChildSplit(key, ch.value, row.id, 'unit', e.target.value)}
                                          >
                                            <option value="days">days</option>
                                            <option value="weeks">weeks</option>
                                            <option value="months">months</option>
                                            <option value="years">years</option>
                                          </select>
                                        </div>
                                        <div>
                                          <button className="btn btn-secondary" onClick={() => removeChildSplit(key, ch.value, row.id)} title="Remove">×</button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              {modalError && <div style={{ color:'#fca5a5', marginTop:8 }}>{modalError}</div>}
            </div>

            <div style={{ padding:'12px 16px', borderTop:'1px solid #374151', display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : (formMode === 'create' ? 'Create Budget' : 'Save Changes')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetsPage;
